import { Component, type ReactNode, type ErrorInfo } from "react";

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches render-time errors in the React tree so a single bad component
 * does not leave the user staring at a blank screen. Also writes the error
 * to localStorage under `pp.lastError` for post-mortem inspection.
 *
 * Doesn't catch errors in event handlers / async code — those are picked
 * up by the global `window.onerror` + `unhandledrejection` listeners
 * installed in main.tsx.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    try {
      localStorage.setItem(
        "pp.lastError",
        JSON.stringify({
          source: "react-error-boundary",
          message: error.message,
          stack: error.stack,
          componentStack: info.componentStack,
          at: new Date().toISOString(),
          url: window.location.href,
        }),
      );
    } catch {
      /* localStorage may throw on quota; ignore */
    }
    console.error("[ErrorBoundary]", error, info);
  }

  private reload = (): void => {
    window.location.reload();
  };

  private goHome = (): void => {
    window.location.href = "/";
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          padding: "32px 24px",
          fontFamily: "system-ui, sans-serif",
          color: "#fff",
          background: "#0b0b14",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 16,
        }}
      >
        <h2 style={{ fontSize: 28, fontWeight: 900, margin: 0 }}>
          🪦 Щось пішло не так
        </h2>
        <p style={{ opacity: 0.7, margin: 0, lineHeight: 1.5 }}>
          Помилка під час відображення сторінки. Спробуй перезавантажити
          або повернутись на головну.
        </p>
        <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
          <button
            onClick={this.reload}
            style={{
              padding: "12px 20px",
              background: "#fff",
              color: "#000",
              borderRadius: 10,
              fontWeight: 700,
              border: "none",
              fontSize: 16,
              cursor: "pointer",
            }}
          >
            Перезавантажити
          </button>
          <button
            onClick={this.goHome}
            style={{
              padding: "12px 20px",
              background: "transparent",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: 10,
              fontWeight: 600,
              fontSize: 16,
              cursor: "pointer",
            }}
          >
            На головну
          </button>
        </div>
        {this.state.error && (
          <pre
            style={{
              fontSize: 11,
              opacity: 0.4,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              marginTop: 24,
              maxWidth: 480,
            }}
          >
            {this.state.error.message}
          </pre>
        )}
      </div>
    );
  }
}
